import React, { forwardRef, useCallback, useEffect, useRef } from 'react'

import { containerStyles, getWaveformColors } from './Waveform.styles'

export type WaveformProps = {
  /** Color scheme */
  colorScheme?: 'dark' | 'light'
  /** Audio source URL to load into the waveform */
  src?: string
  /** Called when audio finishes playing */
  onFinish?: () => void
  /** Called when WaveSurfer instance is ready */
  onReady?: (ws: unknown) => void
  /** Additional CSS class */
  className?: string
  /** Additional inline styles */
  style?: React.CSSProperties
}

export const Waveform = forwardRef<HTMLDivElement, WaveformProps>(
  ({ colorScheme = 'dark', src, onFinish, onReady, className, style }, ref) => {
    const internalRef = useRef<HTMLDivElement>(null)
    const wsRef = useRef<unknown>(null)
    const colors = getWaveformColors(colorScheme)

    const containerRef = (ref ?? internalRef) as React.RefObject<HTMLDivElement>

    const initWaveSurfer = useCallback(async () => {
      if (!containerRef.current) return

      // Destroy existing instance
      if (wsRef.current) {
        ;(wsRef.current as { destroy: () => void }).destroy()
      }

      const WaveSurfer = (await import('wavesurfer.js')).default
      if (!containerRef.current) return

      const ws = WaveSurfer.create({
        container: containerRef.current,
        waveColor: colors.bar,
        progressColor: colors.barProgress,
        cursorColor: colors.cursor,
        cursorWidth: 1,
        barWidth: 2,
        barGap: 4,
        barRadius: 0,
        responsive: true,
        height: 30,
        normalize: true,
        partialRender: true,
        hideScrollbar: true,
      })

      if (onFinish) {
        ws.on('finish', onFinish)
      }

      if (src) {
        ws.load(src)
      }

      wsRef.current = ws
      onReady?.(ws)

      return ws
    }, [colorScheme, src])

    useEffect(() => {
      initWaveSurfer()
      return () => {
        if (wsRef.current) {
          ;(wsRef.current as { destroy: () => void }).destroy()
        }
      }
    }, [initWaveSurfer])

    const cStyles = containerStyles(colorScheme)

    return (
      <div
        className={className}
        ref={containerRef}
        style={{
          ...cStyles,
          ...style,
        }}
      />
    )
  }
)

Waveform.displayName = 'Waveform'
